import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Users, Briefcase, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Parties() {
  const [parties, setParties] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/parties`, { timeout: 90000 })
      .then((r) => setParties(r.data || []))
      .catch(() => toast.error("Failed to load parties"))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen" style={{ background: "var(--whip-bg)" }}>
      <Navigation />

      <header className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-start gap-3">
            <Briefcase className="w-10 h-10 text-orange-400 shrink-0 mt-1" />
            <div>
              <h1 className="text-4xl font-bold text-gray-900" data-testid="parties-title">
                Parties of the U.S. Congress
              </h1>
              <p className="text-base text-gray-600 mt-1 max-w-3xl">
                Aggregate member counts, tenure, sponsorship activity, geographic distribution, and roll-call cohesion for every party currently represented.
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading && (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mx-auto mb-3" />
            <p className="text-gray-600">Loading party aggregates… (this may take ~30s on first load)</p>
          </div>
        )}

        {!loading && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {parties.map((p) => (
              <Link
                key={p.party}
                to={`/parties/${p.party}`}
                data-testid={`party-card-${p.party}`}
                className="block"
              >
                <Card
                  className="p-6 bg-white rounded-xl border-2 border-gray-200 hover:shadow-lg transition-all h-full"
                  style={{ borderLeftColor: p.color, borderLeftWidth: 6 }}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="text-3xl font-bold text-gray-900">{p.displayName}</p>
                      <p className="text-sm text-gray-500">Party code: {p.party}</p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-gray-400" />
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <Stat label="Total members" value={p.totalMembers} />
                    <Stat label="Avg. tenure" value={`${p.averageYearsInOffice}y`} />
                    <Stat label="House" value={p.byChamber?.House ?? 0} />
                    <Stat label="Senate" value={p.byChamber?.Senate ?? 0} />
                  </div>
                  <Badge className="mt-4 bg-gray-100 text-gray-700 border-gray-200 border">
                    <Users className="w-3 h-3 mr-1" />
                    {p.uniqueCommittees} committee assignments
                  </Badge>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <p className="text-xs text-gray-500 uppercase tracking-wide">{label}</p>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
    </div>
  );
}
